import { axios } from 'lib';
import { useAuth } from 'lib/auth';
import React, { createContext, useState, useEffect, useContext } from 'react';

export const defaultFilters = {
  includeInactive: false,
  includeExpired: false,
  sortBy: 'dateCreated',
  scope: 'mine',
  competenceAreas: [],
  place: null,
  radiusKm: 25,
  ownerTypes: [],
  buyerID: null,
  buyerName: '',
  starred: false,
};

const persistedFilterKeys = ['scope', 'competenceAreas', 'place', 'radiusKm', 'ownerTypes', 'buyerID', 'buyerName', 'starred'];

const FiltersContext = createContext();

export const FiltersProvider = ({ children }) => {
  const { user } = useAuth();
  const [filters, setFilters] = useState(defaultFilters);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const getFilters = async () => {
      if (user) {
        const {
          data,
        }  = await axios.get('/api/users/preferences');

        if (data && data?.filters) {
          const { locations, ...savedFilters } = data.filters;
          setFilters(currentDefaultFilters => ({
            ...currentDefaultFilters,
            ...savedFilters,
          }));
        }

        setIsLoaded(true);
      } else {
        setFilters(defaultFilters);
        setIsLoaded(true);
      }
    }

    getFilters();
  }, [user]);

  useEffect(() => {
    const saveFilters = async () => {
      if (user && isLoaded) {
        const filtersToSave = {};
        for (const key of persistedFilterKeys) {
          if (filters.hasOwnProperty(key)) {
            filtersToSave[key] = filters[key];
          }
        }

        if (Object.keys(filtersToSave).length > 0) {
          await axios.put('/api/users/preferences', { filters: filtersToSave });
        }
      }
    }

    saveFilters();
  }, [filters, user]);

  const contextValue = { filters, setFilters, defaultFilters, isLoaded };

  return (
    <FiltersContext.Provider value={contextValue}>
      {children}
    </FiltersContext.Provider>
  );
};

/**
 * A custom hook to easily access the FiltersContext.
 */
// export const useFilters = () => useContext(FiltersContext);

export const useFilters = () => {
  const context = useContext(FiltersContext);
  
  // If the context is undefined, it means we're not inside a provider.
  if (context === undefined) {
    // Throw a helpful error that tells the developer exactly what to do.
    throw new Error('useFilters must be used within a FiltersProvider');
  }
  
  return context;
};